import { configureStore } from '@reduxjs/toolkit';
import { IApiState } from 'src/infra/stores/reducer/apiSlice';
import { ISearchState } from 'src/infra/stores/reducer/searchSlice';
import { IBilledEntitiesState } from 'src/infra/stores/reducer/billedEntitiesSlice';
import { ISSEState } from 'src/infra/stores/reducer/sseSlice';
import { ApiVersionState } from './reducer/apiVersionSlice';

import { storage } from './localStorage';
import reducer from './reducer/index';
import { AuthState } from './reducer/auth';
import { DeleteModeState } from './reducer/deleteModeSlice';

export interface IRootState {
  readonly auth: AuthState;
  readonly api: IApiState;

  readonly search: ISearchState;
  readonly billedEntities: IBilledEntitiesState;
  readonly apiVersion: ApiVersionState;
  readonly deleteMode: DeleteModeState;
  readonly sse: ISSEState;
}

/**
 * load state from local storage
 */
const loadState = (): IRootState => {
  try {
    const localStorage = storage.get(import.meta.env.VITE_LOCAL_STORAGE_NAME);

    if (localStorage === null) {
      return undefined;
    }
    // Initialize preload data with any available stored states
    let preloadData: Partial<IRootState> = {};

    // Load auth state if available
    if (localStorage?.auth) {
      preloadData = { ...preloadData, auth: localStorage.auth };
    }

    // Load billed entities state if available
    if (localStorage?.billedEntities) {
      preloadData = { ...preloadData, billedEntities: localStorage.billedEntities };
    }

    // Load API version state if available
    if (localStorage?.apiVersion) {
      preloadData = { ...preloadData, apiVersion: localStorage.apiVersion };
    }

    // Load delete mode state if available
    if (localStorage?.deleteMode) {
      preloadData = { ...preloadData, deleteMode: localStorage.deleteMode };
    }

    return preloadData as IRootState;
  } catch (e) {
    return undefined;
  }
};

// Create the store instance
const createStore = (preloadedState: Partial<IRootState> = loadState()) => {
  const store = configureStore({
    reducer,
    preloadedState,
    // middleware: (getDefaultMiddleware) => getDefaultMiddleware({
    //   serializableCheck: false
    // }).concat(logger)
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
      }),
  });
  // save to local storage if redux state is changed
  store.subscribe(() => {
    let currentStorage = storage.get(import.meta.env.VITE_LOCAL_STORAGE_NAME);
    if (!currentStorage) {
      currentStorage = {};
    }

    storage.store(import.meta.env.VITE_LOCAL_STORAGE_NAME, {
      ...currentStorage,
      // auth: store.getState().auth,
      billedEntities: store.getState().billedEntities,
      apiVersion: store.getState().apiVersion,
      deleteMode: store.getState().deleteMode,
    });
  });

  return store;
};

// Export the store instance to be used by interceptors
export const store = createStore();

// For backward compatibility
// eslint-disable-next-line arrow-body-style
export const initStore = (preloadedState: Partial<IRootState> = loadState()) => {
  return createStore(preloadedState);
};

export type AppDispatch = ReturnType<typeof initStore>['dispatch'];
